import React, { useMemo, useState } from 'react';
import { TextField, Menu, MenuItem, Collapse, List, Box, ClickAwayListener } from '@mui/material';
import { ExpandMore, ChevronRight, ArrowDropDown, ArrowRight } from '@mui/icons-material';
import TextOverflowWithTooltip from '../TextOverflowWithTooltip/TextOverflowWithTooltip';
import { styles } from './AccordianSelectDropdown.styles';
import { IAccordionListNode } from '@opengov/component-library/capital/AccordionList/AccordionList';

export interface IAccordianSelectDropdownProps {
  data: IAccordionListNode[];
  maxHeight: React.CSSProperties['maxHeight'];
  onSelect: (id: string) => void;
  selectedId: string | null;
  placeholder: string;
}

export const noneNode: IAccordionListNode = { id: 'none', displayName: 'None' };

export const AccordianSelectDropdown = ({
  data,
  maxHeight,
  onSelect,
  selectedId,
  placeholder
}: IAccordianSelectDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});

  const dataWithNoneNode = useMemo(() => [noneNode, ...data], [data]);

  const selectedItem: IAccordionListNode | undefined = useMemo(() => {
    let selectedItem: IAccordionListNode | undefined;
    const findSelectedItem = (nodes: IAccordionListNode[]) => {
      nodes.forEach((node) => {
        if (node.children) {
          findSelectedItem(node.children);
        } else if (node.id === selectedId) {
          selectedItem = node;
          return;
        }
      });
    };
    findSelectedItem(dataWithNoneNode);
    return selectedItem;
  }, [selectedId, dataWithNoneNode]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setExpandedMap({});
  };

  const handleSelect = (id: string) => {
    handleClose();
    onSelect(id);
  };

  const toggleGroup = (id: string) => {
    setExpandedMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderMenuItems = (nodes: IAccordionListNode[], level = 0) => {
    return nodes.map((node) => {
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      const isExpanded = expandedMap[node.id] || false;

      return (
        <Box key={node.id}>
          <MenuItem
            onClick={() => (hasChildren ? toggleGroup(node.id) : handleSelect(node.id))}
            sx={{ ...styles.menuItem, paddingLeft: `${level * 16 + 8}px` }}
          >
            <TextOverflowWithTooltip>{node.displayName}</TextOverflowWithTooltip>
            {hasChildren && (isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />)}
          </MenuItem>
          {hasChildren && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List disablePadding>{renderMenuItems(node.children || [], level + 1)}</List>
            </Collapse>
          )}
        </Box>
      );
    });
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={styles.container}>
        <TextField
          sx={styles.textField}
          fullWidth
          placeholder={placeholder}
          value={selectedItem?.displayName || noneNode.displayName}
          onClick={handleClick}
          InputProps={{
            readOnly: true,
            endAdornment: anchorEl ? <ArrowDropDown fontSize="small" /> : <ArrowRight fontSize="small" />
          }}
        />

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { style: { width: anchorEl?.clientWidth, maxHeight } } }}
        >
          {renderMenuItems(dataWithNoneNode)}
        </Menu>
      </Box>
    </ClickAwayListener>
  );
};
